class BRCollectionQuickAdd extends HTMLElement {
  connectedCallback() {
    if (this.dataset.brInitialized === 'true') return;

    this.dataset.brInitialized = 'true';

    this.variantInput = this.querySelector('input[name="id"]');
    this.optionSelects = Array.from(
      this.querySelectorAll('[data-br-option]')
    ).sort(
      (firstSelect, secondSelect) =>
        Number(firstSelect.dataset.optionPosition) -
        Number(secondSelect.dataset.optionPosition)
    );

    this.button = this.querySelector('[ref="addToCartButton"]');
    this.buttonLabel = this.querySelector('[data-br-add-label]');
    this.buttonContainer = this.querySelector(
      '[ref="addToCartButtonContainer"]'
    );

    const variantsScript = this.querySelector('[data-br-variants]');

    try {
      this.variants = JSON.parse(variantsScript?.textContent || '[]');
    } catch (error) {
      console.error(
        '[BR collection quick add] Unable to parse product variants.',
        error
      );

      this.variants = [];
    }

    this.optionSelects.forEach((select) => {
      select.addEventListener('change', this.handleOptionChange);
    });

    /*
     * Prevent clicks on selectors and Add to cart from bubbling to
     * Horizon's product-card navigation handler.
     *
     * Internal Product Form and Add to Cart handlers run before the event
     * reaches this custom element, so native cart behavior remains intact.
     */
    this.addEventListener('click', this.stopProductCardNavigation);

    this.syncVariant();
  }

  disconnectedCallback() {
    this.optionSelects?.forEach((select) => {
      select.removeEventListener('change', this.handleOptionChange);
    });

    this.removeEventListener('click', this.stopProductCardNavigation);
  }

  handleOptionChange = () => {
    this.syncVariant();
  };

  stopProductCardNavigation = (event) => {
    event.stopPropagation();
  };

  getSelectedOptionValues() {
    return this.optionSelects.map((select) => select.value);
  }

  findSelectedVariant() {
    if (!this.variantInput) return null;

    /*
     * Products with only the default variant have no visible selectors.
     */
    if (this.optionSelects.length === 0) {
      const currentVariantId = String(this.variantInput.value || '');

      return (
        this.variants.find(
          (variant) => String(variant.id) === currentVariantId
        ) ||
        this.variants[0] ||
        null
      );
    }

    const selectedOptions = this.getSelectedOptionValues();

    if (selectedOptions.some((value) => !value)) {
      return null;
    }

    return (
      this.variants.find((variant) => {
        if (
          !Array.isArray(variant.options) ||
          variant.options.length !== selectedOptions.length
        ) {
          return false;
        }

        return variant.options.every(
          (optionValue, optionIndex) =>
            String(optionValue) === selectedOptions[optionIndex]
        );
      }) || null
    );
  }

  syncVariant() {
    if (!this.variantInput || !this.button || !this.buttonLabel) return;

    const hasIncompleteSelection = this.optionSelects.some(
      (select) => !select.value
    );

    const selectedVariant = hasIncompleteSelection
      ? null
      : this.findSelectedVariant();

    const isAvailable = Boolean(selectedVariant?.available);

    if (selectedVariant) {
      this.variantInput.value = String(selectedVariant.id);
      this.variantInput.disabled = false;
      this.dataset.variantId = String(selectedVariant.id);
    } else {
      this.variantInput.value = '';
      this.variantInput.disabled = true;
      delete this.dataset.variantId;
    }

    this.button.disabled = !isAvailable;

    if (hasIncompleteSelection) {
      this.buttonLabel.textContent =
        this.dataset.selectLabel || 'Choose options';
    } else if (!selectedVariant) {
      this.buttonLabel.textContent =
        this.dataset.unavailableLabel || 'Unavailable';
    } else if (!isAvailable) {
      this.buttonLabel.textContent =
        this.dataset.soldOutLabel || 'Sold out';
    } else {
      this.buttonLabel.textContent =
        this.dataset.addLabel || 'Add to cart';
    }

    /*
     * Only update the image used by Horizon's fly-to-cart animation.
     * This does not change or replace the product card gallery image.
     */
    const variantMediaUrl =
      selectedVariant?.featured_image?.src ||
      selectedVariant?.featured_media?.preview_image?.src ||
      selectedVariant?.featured_media?.preview_image?.url;

    if (variantMediaUrl && this.buttonContainer) {
      const separator = variantMediaUrl.includes('?') ? '&' : '?';

      this.buttonContainer.dataset.productVariantMedia =
        `${variantMediaUrl}${separator}width=100`;
    }
  }
}

if (!customElements.get('br-collection-quick-add')) {
  customElements.define(
    'br-collection-quick-add',
    BRCollectionQuickAdd
  );
}